import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ExternalLink } from 'lucide-react';
import { SiLinkedin, SiFacebook, SiInstagram, SiWhatsapp } from 'react-icons/si';
import { NAV_ITEMS } from '@/data/index';
import { ROUTE_PATHS } from '@/lib/index';
import { useLang } from '@/hooks/useLang';
import GDPRConsent from '@/components/GDPRConsent';
import NewsletterBar from '@/components/NewsletterBar';

/* ── ScrollToTop — la orice schimbare de rută, mergi la vârful paginii ──── */
function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

/* ── Flag Language Switcher ──────────────────────────────────────────────── */
function LangSwitcher() {
  const { lang, setLang } = useLang();
  /* Show the flag image of the language currently ACTIVE, with its 2-letter code */
  const current = lang === 'ro'
    ? { flagSrc: 'https://flagcdn.com/w20/ro.png', flagSrc2x: 'https://flagcdn.com/w40/ro.png', code: 'RO', next: 'Switch to EN' }
    : { flagSrc: 'https://flagcdn.com/w20/us.png', flagSrc2x: 'https://flagcdn.com/w40/us.png', code: 'EN', next: 'Schimbă în RO' };

  return (
    <button onClick={() => setLang(lang === 'ro' ? 'en' : 'ro')}
      className="flex items-center gap-1.5 px-2 py-1 rounded transition-all hover:bg-white/5 shrink-0"
      title={current.next}>
      <img
        src={current.flagSrc}
        srcSet={`${current.flagSrc} 1x, ${current.flagSrc2x} 2x`}
        alt={current.code}
        width={20}
        height={15}
        className="rounded-sm object-cover"
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
      />
      <span className="text-[10px] tracking-widest text-white/55 uppercase font-semibold">{current.code}</span>
    </button>
  );
}

/* ── Social sidebar — fixed, floating, brand colors on hover ─────────────── */
const SOCIALS = [
  { Icon: SiLinkedin,  href: 'https://linkedin.com/company/interpersonal-ro',  color: '#0A66C2', label: 'LinkedIn'  },
  { Icon: SiFacebook,  href: 'https://facebook.com/interpersonal.ro',          color: '#1877F2', label: 'Facebook'  },
  { Icon: SiInstagram, href: 'https://instagram.com/interpersonal.ro',         color: '#E4405F', label: 'Instagram' },
  { Icon: SiWhatsapp,  href: 'https://wa.me/40745032222',                      color: '#25D366', label: 'WhatsApp'  },
];

function SocialSidebar() {
  return (
    /* Always visible: fixed left sidebar regardless of scroll position */
    <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50 hidden xl:flex flex-col gap-4">
      {SOCIALS.map(({ Icon, href, color, label }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer"
          aria-label={label}
          className="social-icon w-7 h-7 flex items-center justify-center text-white/25"
          style={{ '--sc': color } as React.CSSProperties}
          onMouseEnter={e => { e.currentTarget.style.color = color; }}
          onMouseLeave={e => { e.currentTarget.style.color = ''; }}>
          <Icon size={15} />
        </a>
      ))}
    </div>
  );
}

/* ── Header ──────────────────────────────────────────────────────────────── */
function Header() {
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang, t }  = useLang();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  /* Service items only — no Home, no Contact */
  const serviceItems = NAV_ITEMS.filter(i =>
    i.route !== ROUTE_PATHS.HOME && i.route !== ROUTE_PATHS.CONTACT
  );

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${scrolled ? 'bg-[#0a0c12]/92 backdrop-blur-xl border-b border-white/5 shadow-lg' : 'bg-transparent'}`}>
      <nav className="container mx-auto px-6 flex items-center justify-between h-14 gap-2">
        {/* Logo → Home */}
        <Link to={ROUTE_PATHS.HOME} className="flex items-center shrink-0 group">
          <img src="https://interpersonal.ro/uploads/logo/inter+logo.png" alt="INTERPERSONAL"
            className="h-7 w-auto object-contain transition-opacity group-hover:opacity-80"
            style={{ filter: 'brightness(1.1)' }} />
        </Link>

        {/* Desktop nav — service items only, single row */}
        <div className="hidden lg:flex items-center gap-0 text-[11px] flex-1 justify-center">
          {serviceItems.map(item => (
            <NavLink key={item.route} to={item.route}
              className={({ isActive }) => `px-2.5 py-1.5 font-medium tracking-wide transition-colors whitespace-nowrap rounded ${isActive ? 'text-[#4ab8ff]' : 'text-white/48 hover:text-white/85'}`}>
              {t(item.labelRo, item.labelEn)}
            </NavLink>
          ))}
        </div>

        {/* Right: lang + CTA */}
        <div className="flex items-center gap-2 shrink-0">
          <LangSwitcher />
          <a href="https://websiteconfigurator.interpersonal.ro/" target="_blank" rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-semibold btn-outline-gradient hover:scale-105 transition-transform whitespace-nowrap">
            {t('Începe proiectul', 'Start Project')}
          </a>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-1.5 text-white/60 hover:text-white">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0a0c12] border-t border-white/5 overflow-hidden">
            <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
              {serviceItems.map(item => (
                <NavLink key={item.route} to={item.route} onClick={() => setOpen(false)}
                  className={({ isActive }) => `px-4 py-2.5 text-sm tracking-wide transition-colors rounded ${isActive ? 'text-[#4ab8ff] bg-[#4ab8ff]/5' : 'text-white/55 hover:text-white/90'}`}>
                  {t(item.labelRo, item.labelEn)}
                </NavLink>
              ))}
              <NavLink to={ROUTE_PATHS.CONTACT} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-4 py-2.5 text-sm tracking-wide transition-colors rounded ${isActive ? 'text-[#4ab8ff] bg-[#4ab8ff]/5' : 'text-white/55 hover:text-white/90'}`}>
                {t('Contact', 'Contact')}
              </NavLink>
              <a href="https://websiteconfigurator.interpersonal.ro/" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
                className="mt-2 py-2.5 rounded text-xs font-semibold text-center btn-gradient">
                {t('Configurator', 'Configurator')}
              </a>
              <button onClick={() => { setLang(lang === 'ro' ? 'en' : 'ro'); setOpen(false); }}
                className="py-2 text-xs text-white/35 flex items-center justify-center gap-2 tracking-widest">
                {lang === 'ro'
                  ? <><img src="https://flagcdn.com/w20/us.png" srcSet="https://flagcdn.com/w20/us.png 1x, https://flagcdn.com/w40/us.png 2x" alt="EN" width={20} height={15} className="rounded-sm inline-block" /> Switch to EN</>
                  : <><img src="https://flagcdn.com/w20/ro.png" srcSet="https://flagcdn.com/w20/ro.png 1x, https://flagcdn.com/w40/ro.png 2x" alt="RO" width={20} height={15} className="rounded-sm inline-block" /> Schimbă în RO</>}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ── Footer ──────────────────────────────────────────────────────────────── */
function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();
  const PHONES = ['+40 758 011 010', '+40 745 032 222', '+40 741 407 595'];
  const ADDRESS = 'Str. Orhideelor nr. 8, Sat Țegheș, Com. Domnești, Jud. Ilfov, 077091';
  const ADDRESS_MAP = 'https://maps.google.com/?q=Str.+Orhideelor+nr.+8,+Sat+Teghes,+Com.+Domnesti,+Jud.+Ilfov,+Romania';
  const WAZE = 'https://waze.com/ul?q=Interpersonal+SRL+Ilfov+Romania';

  const LEGAL = [
    { ro: 'Termeni și Condiții',        en: 'Terms & Conditions',   route: '/termeni' },
    { ro: 'Politică Confidențialitate', en: 'Privacy Policy',       route: '/confidentialitate' },
    { ro: 'Politică Retur',             en: 'Return Policy',        route: '/retur' },
    { ro: 'Acord Confidențialitate',    en: 'Confidentiality NDA',  route: '/nda' },
    { ro: 'Politică Cookies',           en: 'Cookie Policy',        route: '/cookies' },
  ];

  return (
    <footer className="bg-[#07090f] border-t border-white/5">
      <div className="container mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <img src="https://interpersonal.ro/uploads/logo/inter+logo.png" alt="INTERPERSONAL"
                className="h-7 w-auto object-contain opacity-88" style={{ filter: 'brightness(1.05)' }} />
            </div>
            <p className="text-xs text-white/32 leading-relaxed mb-3">
              {t('Furnizor complet de servicii și produse IT. ISO 9001 & ISO 27001 certificat.','Complete IT services & products provider. ISO 9001 & ISO 27001 certified.')}
            </p>
            <div className="text-[10px] text-white/18 mb-4 space-y-0.5">
              <div>INTERPERSONAL S.R.L. · CUI: RO34799221</div>
              <div>Nr. înreg.: J2015002538235</div>
              <div>EUID: ROONRC.J2015002538235</div>
              <div>Ilfov, România · Fondată 2015</div>
            </div>
            <div className="flex gap-2.5">
              {SOCIALS.map(({ Icon, href, color, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  className="social-icon w-8 h-8 rounded border border-white/8 flex items-center justify-center text-white/22"
                  style={{ '--sc': color } as React.CSSProperties}
                  onMouseEnter={e => (e.currentTarget.style.color = color)}
                  onMouseLeave={e => (e.currentTarget.style.color = '')}>
                  <Icon size={13} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="section-number mb-4">{t('Servicii', 'Services')}</h3>
            <ul className="space-y-1.5">
              {NAV_ITEMS.filter(i => i.route !== ROUTE_PATHS.HOME && i.route !== ROUTE_PATHS.CONTACT).map(item => (
                <li key={item.route}>
                  <NavLink to={item.route} className="text-xs text-white/32 hover:text-[#4ab8ff] transition-colors">
                    {t(item.labelRo, item.labelEn)}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2 border-t border-white/5">
                <Link to={ROUTE_PATHS.CLIENTS} className="text-xs text-white/32 hover:text-[#4ab8ff] transition-colors">
                  {t('Clienții Noștri', 'Our Clients')} ↗
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-xs text-white/32 hover:text-[#4ab8ff] transition-colors">
                  {t('Blog IT & Tehnologie', 'IT & Technology Blog')} ↗
                </Link>
              </li>
              <li>
                <a href="https://websiteconfigurator.interpersonal.ro/" target="_blank" rel="noopener noreferrer"
                  className="text-xs text-white/32 hover:text-[#4ab8ff] transition-colors flex items-center gap-1">
                  {t('Configurator Preț','Price Configurator')} <ExternalLink size={9}/>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact — clickable phone, address (maps), email */}
          <div>
            <h3 className="section-number mb-4">{t('Contact','Contact')}</h3>
            <div className="space-y-2 text-xs text-white/32">
              {/* Clickable address — opens Google Maps / Waze */}
              <div className="leading-relaxed">
                <a href={ADDRESS_MAP} target="_blank" rel="noopener noreferrer"
                  className="hover:text-[#4ab8ff] transition-colors" title="Google Maps">
                  {ADDRESS.split(',').map((part, i) => <span key={i}>{part.trim()}{i < 4 ? <br/> : ''}</span>)}
                </a>
              </div>
              {/* Clickable phones */}
              <div className="space-y-1 pt-1">
                {PHONES.map(p => (
                  <a key={p} href={`tel:${p.replace(/\s/g,'')}`} className="block hover:text-[#4ab8ff] transition-colors">
                    {p}
                  </a>
                ))}
              </div>
              {/* Clickable email */}
              <a href="mailto:office@interpersonal.ro" className="block hover:text-[#4ab8ff] transition-colors">
                office@interpersonal.ro
              </a>
              {/* Link pagina Contact */}
              <Link to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-1 text-[#4ab8ff]/70 hover:text-[#4ab8ff] transition-colors font-medium">
                <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/></svg>
                {t('Pagina de Contact', 'Contact Page')}
              </Link>
              {/* Waze + Quick Call icons */}
              <div className="flex items-center gap-5 pt-3 border-t border-white/5">
                {/* Waze */}
                <a href={WAZE} target="_blank" rel="noopener noreferrer"
                  title="Deschide în Waze"
                  className="flex flex-col items-center gap-1 group">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center border border-[#4ab8ff]/30 group-hover:border-[#4ab8ff]/70 transition-all"
                    style={{ background: 'rgba(74,184,255,0.08)' }}>
                    {/* Waze ghost SVG — monocrom #4ab8ff */}
                    <svg viewBox="0 0 64 64" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg"
                      className="opacity-70 group-hover:opacity-100 transition-opacity">
                      <path d="M32 4C18.7 4 8 14.7 8 28c0 7.5 3.4 14.2 8.7 18.8l-1.5 8.4a1 1 0 0 0 1.4 1.1l8-3.8A24 24 0 0 0 32 53c13.3 0 24-10.7 24-24S45.3 4 32 4z" fill="#4ab8ff" opacity="0.18"/>
                      <path d="M32 6C19.8 6 10 15.8 10 28c0 7.2 3.4 13.6 8.7 17.8l-1.4 7.7a1 1 0 0 0 1.4 1.1l7.5-3.6A22 22 0 0 0 32 52c12.2 0 22-9.8 22-24S44.2 6 32 6z" stroke="#4ab8ff" strokeWidth="2" fill="none"/>
                      <circle cx="24" cy="28" r="3.5" fill="#4ab8ff"/>
                      <circle cx="40" cy="28" r="3.5" fill="#4ab8ff"/>
                      <path d="M25 37c1.8 2.5 12.2 2.5 14 0" stroke="#4ab8ff" strokeWidth="2" strokeLinecap="round" fill="none"/>
                      <circle cx="44" cy="20" r="3" fill="#4ab8ff" opacity="0.5"/>
                    </svg>
                  </span>
                  <span className="text-[9px] tracking-widest uppercase font-semibold text-white/35 group-hover:text-[#4ab8ff] transition-colors">Waze</span>
                </a>
                {/* Call +40 741 407 595 */}
                <a href="tel:+40741407595"
                  title="Apelează +40 741 407 595"
                  className="flex flex-col items-center gap-1 group">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center border border-[#4ab8ff]/30 group-hover:border-[#4ab8ff]/70 transition-all"
                    style={{ background: 'rgba(74,184,255,0.08)' }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"
                      className="text-[#4ab8ff] opacity-75 group-hover:opacity-100 transition-opacity">
                      <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.61 21 3 13.39 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.58a1 1 0 0 1-.24 1.01l-2.21 2.2z"/>
                    </svg>
                  </span>
                  <span className="text-[9px] tracking-widest uppercase font-semibold text-white/35 group-hover:text-[#4ab8ff] transition-colors">
                    {t('Sună!', 'Call!')}
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Informații Utile */}
          <div>
            <h3 className="section-number mb-4">{t('Informații Utile','Useful Information')}</h3>
            <p className="text-xs text-white/32 leading-relaxed mb-4">
              {t('Calculați costul site-ului dvs. cu configuratorul interactiv în 7 pași.','Calculate your website cost with our interactive 7-step configurator.')}
            </p>
            <a href="https://websiteconfigurator.interpersonal.ro/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded text-xs font-semibold btn-gradient mb-5">
              {t('Configurator','Configurator')} <ExternalLink size={10}/>
            </a>
            <div className="text-[10px] text-white/18 leading-relaxed mb-5">
              <div>CAEN: 6220 · <a href="https://interpersonal.ro" className="hover:text-[#4ab8ff] transition-colors">interpersonal.ro</a></div>
            </div>
            {/* Trust badges: Netopia + ANPC SAL + SOL — fond alb ca pe apifarma */}
            <div className="flex flex-col gap-2 mt-1">
              {/* Netopia Payments */}
              <a href="https://netopia.ro" target="_blank" rel="noopener noreferrer"
                title="Netopia Payments"
                className="inline-flex items-center justify-center rounded-md hover:opacity-90 transition-opacity"
                style={{ background: '#ffffff', padding: '4px 8px', maxWidth: '155px' }}>
                <img src="/netopia.svg" alt="Netopia Payments" className="h-6 w-auto object-contain" />
              </a>
              {/* ANPC SAL */}
              <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer"
                title="ANPC — Soluționarea Alternativă a Litigiilor"
                className="inline-flex items-center justify-center rounded-md hover:opacity-90 transition-opacity"
                style={{ background: '#ffffff', padding: '4px 8px', maxWidth: '185px' }}>
                <img src="/sal.png" alt="ANPC SAL" className="h-8 w-auto object-contain" />
              </a>
              {/* SOL */}
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer"
                title="SOL — Soluționarea Online a Litigiilor"
                className="inline-flex items-center justify-center rounded-md hover:opacity-90 transition-opacity"
                style={{ background: '#ffffff', padding: '4px 8px', maxWidth: '185px' }}>
                <img src="/sol.png" alt="SOL" className="h-8 w-auto object-contain" />
              </a>
            </div>
          </div>
        </div>

        {/* Legal quick links row */}
        <div className="mt-10 pt-5 border-t border-white/5 flex flex-wrap gap-x-5 gap-y-2 justify-center">
          {LEGAL.map(l => (
            <Link key={l.route} to={l.route} className="text-[10px] text-white/22 hover:text-white/55 transition-colors tracking-wide">
              {t(l.ro, l.en)}
            </Link>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-white/4 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] text-white/15">
          <p>© {year} INTERPERSONAL S.R.L. · CUI RO34799221 · J2015002538235 · EUID ROONRC.J2015002538235</p>
          <p>{t('Toate drepturile rezervate.','All rights reserved.')}</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#0a0c12] text-foreground">
      <ScrollToTop />
      <Header />
      <SocialSidebar />
      <main className="flex-1">{children}</main>
      {/* Newsletter bar just above footer, on every page */}
      <NewsletterBar />
      <Footer />
      {/* GDPR consent banner */}
      <GDPRConsent />
    </div>
  );
}
